import { useState, useRef, useEffect, useMemo } from "react";
import Image from "components/Image";
import translation from "./translation";
import { useLocale } from "hooks";
import clsx from "clsx";

const FaqItem = ({ item, onToggle, open }) => {
  const [_, setState] = useState<boolean>(false);
  const { locale, isRTL } = useLocale();
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const callBack = () => setState((prev) => !prev);
    window.addEventListener("resize", callBack);
    return () => window.removeEventListener("resize", callBack);
  }, []);

  const mappedData = useMemo<Array<[string, string]>>(() => {
    const data: Record<string, string> = {
      [translation[locale]?.WRITTEN_BY]: item?.writer,
      [translation[locale]?.TRANSLATION]: item?.translator,
      [translation[locale]?.PAGE_COUNT]: item?.pageCount,
    };
    return Object.entries(data).filter(([_, value]) => Boolean(value));
  }, [item]);

  const mappedLinks = useMemo<Array<[string, string]>>(() => {
    const data = {
      [translation[locale]?.PREVIEW_LINK]: item?.previewLink,
      [translation[locale]?.PURCHASE_LINK]: item?.purchaseLink,
    };
    return Object.entries(data).filter(([_, value]) => Boolean(value?.label));
  }, [item]);

  return (
    <div
      className={`tw-py-2 md:tw-py-3 xxxl:tw-py-[17.33px] tw-px-4 md:tw-px-6 tw-rounded-lg tw-mb-4 last:tw-mb-0 tw-transition-all tw-duration-500 tw-border-t-0 tw-border-l-0 tw-border-r-0 tw-bg-[white] ${
        open
          ? "tw-border tw-border-solid !tw-border-[#007FAC] md:!tw-border-r-2 md:!tw-border-l-2 md:!tw-border-t-2 !tw-border-b-2 tw-shadow-lg"
          : "tw-border tw-border-solid tw-border-[#E5E9F4] md:!tw-border-r md:!tw-border-l md:!tw-border-t !tw-border-b"
      }`}
    >
      <div
        className="tw-flex tw-justify-between tw-items-center tw-cursor-pointer"
        onClick={() => onToggle()}
      >
        <div className="tw-relative">
          <h6
            className={`tw-text-[14px] tw-font-medium tw-transition-all ${
              open ? "tw-text-[#007FAC]" : "tw-text-[#344456]/60"
            }`}
          >
            {item?.title}
          </h6>
        </div>

        {open ? (
          <div className="tw-mt-1 tw-rounded-full tw-p-3 tw-flex tw-justify-center tw-items-center tw-h-[30px] tw-w-[30px] tw-bg-[#007FAC]">
            <svg
              width="9"
              height="6"
              viewBox="0 0 9 6"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M7.56805 4.52854L4.53627 1.49745L1.50546 4.57025"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeّinejoin="round"
              />
            </svg>
          </div>
        ) : (
          <div className="tw-mt-1 tw-rounded-full tw-flex tw-justify-center tw-items-center tw-min-h-[30px] tw-min-w-[30px] tw-bg-[#E7ECF4]">
            <svg
              width="9"
              height="6"
              viewBox="0 0 9 6"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M1.43934 1.45031L4.4502 4.50218L7.50207 1.45031"
                stroke="#9EB1CC"
                strokeWidth="2"
                strokeLinecap="round"
                strokeّinejoin="round"
              />
            </svg>
          </div>
        )}
      </div>
      <div
        style={{ height: open ? (ref.current?.clientHeight || 0) + 18 : 0 }}
        className={`tw-transition-all tw-delay-[0ms] tw-duration-500 tw-overflow-hidden tw-relative`}
      >
        <div
          className="tw-text-tiny md:tw-text-md tw-text-[#344456] tw-h-fit tw-pt-[18px] tw-flex tw-flex-col md:tw-flex-row"
          ref={ref}
        >
          <div className="tw-max-w-[140px] tw-max-h-[210px] tw-mx-auto md:tw-mx-[unset]">
            <Image
              src={item?.image}
              width={140}
              height={210}
              className="tw-bg-[#D9D9D9] tw-rounded-[8px] tw-object-cover"
              alt={item?.alt}
            />
          </div>

          <div
            className={clsx(
              "tw-text-[14px] tw-mt-[30px] md:tw-mt-0 tw-flex tw-flex-col tw-justify-between",
              { "md:tw-mr-[30px]": isRTL, "md:tw-ml-[30px]": !isRTL }
            )}
          >
            {mappedData.map(([key, value]) => (
              <div
                key={key}
                className="tw-mb-5 last:tw-mb-0 tw-leading-6 tw-font-medium"
              >
                {key}: {value}
              </div>
            ))}

            {mappedLinks.map(([key, value]) => (
              <div
                key={key}
                className="tw-mb-5 last:tw-mb-0 tw-leading-6 tw-font-medium"
              >
                {key}:{" "}
                {value?.link ? (
                  <a
                    className="tw-text-[#007FAC] hover:tw-text-[#007FAC]/80"
                    href={value?.link}
                  >
                    {value?.label}
                  </a>
                ) : (
                  <span className="tw-text-[#007FAC] hover:tw-text-[#007FAC]/80">
                    {value?.label}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FaqItem;
